import React, { lazy, Suspense, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { TenantProvider } from './contexts/TenantContext';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedLayout from './components/Layout/ProtectedLayout';
import SuperadminLayout from './components/Layout/SuperadminLayout';
import { AppLayoutSkeleton } from './components/ui/UiComponents';

const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
import { renderRouteElements, SUPERADMIN_ROUTES, PROTECTED_ROUTES } from './navigation/routeConfig';

/**
 * Pull-to-refresh hook for Capacitor Android
 * Uses touch events to detect downward swipe from top and trigger a page reload.
 */
function usePullToRefresh() {
  useEffect(() => {
    let startY = 0;
    let isPulling = false;

    const handleTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY;
      // Only allow pull when the page is scrolled to the top
      if (window.scrollY === 0) {
        isPulling = true;
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!isPulling) return;
      const endY = e.changedTouches[0].clientY;
      const delta = endY - startY;
      // If the user pulled down more than 80px, reload the page
      if (delta > 80) {
        window.location.reload();
      }
      isPulling = false;
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);
}

/**
 * Main React Application Route Manager
 */
function App() {
  usePullToRefresh();

  return (
    <TenantProvider>
      <AuthProvider>
        <Router>
          <div className="app-shell">
            <Suspense fallback={<AppLayoutSkeleton />}>
              <Routes>
                {/* Public Access Channels */}
                <Route path="/" element={<Login />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Superadmin System Management Channels */}
                <Route path="/superadmin/login" element={<Navigate to="/login" replace />} />
                <Route element={<SuperadminLayout />}>
                  {renderRouteElements(SUPERADMIN_ROUTES)}
                </Route>

                {/* Tenant-Isolated Guarded Channels */}
                <Route element={<ProtectedLayout />}>
                  {renderRouteElements(PROTECTED_ROUTES)}
                </Route>
              </Routes>
            </Suspense>
          </div>
        </Router>
      </AuthProvider>
    </TenantProvider>
  );
}

export default App;
